// @file Composable that provides access to the Tldraw editor instance and allows registering reactive effects.
import window from '@@/bits/global'
import type { Editor } from '@tldraw/editor'
import { react } from '@tldraw/editor'
import { createSharedComposable, reactiveComputed } from '@vueuse/core'
import { uniqueId } from 'es-toolkit/compat'
import { shallowRef } from 'vue'

type TldrawEffectCallback = (editor: Editor) => void
type StopFunction = () => void

export const useTldrawEditor = createSharedComposable(() => {
  // Using a ref to store the Tldraw editor object will break its internals. Use a shallowRef instead.
  const tldrawEditor = shallowRef<Editor | null>(null)

  // We need two separate Sets to track effects and listeners:
  // 1. registeredListeners stores the original listener functions to prevent duplicate registrations
  // 2. tldrawEffects stores the wrapped reactive versions created by react()
  // We can't use tldrawEffects to detect duplicates because react() creates a new function reference each time
  const registeredListeners = new Set<TldrawEffectCallback>()
  const tldrawEffects = new Set<TldrawEffectCallback>()

  const reactToTldraw = (listener: TldrawEffectCallback): StopFunction => {
    if (registeredListeners.has(listener)) return () => {}
    registeredListeners.add(listener)

    let stopReact: StopFunction

    const effect: TldrawEffectCallback = (editor) => {
      // react() requires a unique name for each effect.
      // We generate one here to abstract this detail from the caller.
      const effectName = uniqueId('tldrawEffect')
      stopReact = react(effectName, () => listener(editor))
    }

    tldrawEffects.add(effect)

    // If the editor is already mounted, run the effect immediately.
    if (tldrawEditor.value != null) {
      effect(tldrawEditor.value)
    }

    return () => {
      stopReact?.()
      tldrawEffects.delete(effect)
      registeredListeners.delete(listener)
    }
  }

  const handleTldrawMounted: TldrawEffectCallback = (editor) => {
    if (tldrawEditor.value != null) return
    tldrawEditor.value = editor

    // Only register the effects once Tldraw has mounted.
    for (const effect of tldrawEffects) {
      effect(editor)
    }

    // TODO: For legacy reasons, we'll still do this until all
    // `getEditorObject` calls are replaced with `useTldrawEditor().editor`
    window.$tldrawEditor = editor
  }

  // Use reactiveComputed to wrap the return object in a reactive proxy
  // so we can just call `useTldrawEditor().editor`
  // instead of `useTldrawEditor().editor.value`
  return reactiveComputed(() => ({
    /**
     * The Tldraw editor instance.
     */
    editor: tldrawEditor,
    /**
     * Sets the Tldraw editor instance.
     * Call this function when the Tldraw editor is mounted.
     */
    set: handleTldrawMounted,
    /**
     * Registers a new effect to be run when the Tldraw editor is mounted.
     * @returns A function to stop the effect.
     * @example
     * const stop = useTldrawEditor().react((editor) => {
     *   log('Number of pages:', editor.getPages().length)
     * })
     */
    react: reactToTldraw,
  }))
})
